// Shared fetch timeout helper used by hibernation-aware request flows.
// Why this exists:
// - Native fetch has no built-in timeout.
// - Callers need a stable timeout error type (FetchTimeoutError) to decide retry/wake behavior.
// - We must preserve any caller-provided AbortSignal while still enforcing our own timeout.
export class FetchTimeoutError extends Error {
  constructor(readonly timeoutMs: number) {
    super(`FETCH_TIMEOUT_${timeoutMs}MS`);
    this.name = 'FetchTimeoutError';
  }
}

export async function fetchWithTimeout(
  url: string,
  init: RequestInit = {},
  timeoutMs?: number
): Promise<Response> {
  if (!timeoutMs || timeoutMs <= 0) {
    return fetch(url, init);
  }

  const controller = new AbortController();
  let didTimeout = false;
  const timeoutId = setTimeout(() => {
    didTimeout = true;
    controller.abort();
  }, timeoutMs);
  const sourceSignal = init.signal;
  const abortFromSource = () => controller.abort();

  if (sourceSignal?.aborted) {
    controller.abort();
  } else if (sourceSignal) {
    // Preserve caller cancellation while still enforcing timeout.
    sourceSignal.addEventListener('abort', abortFromSource, {once: true});
  }

  try {
    return await fetch(url, {
      ...init,
      signal: controller.signal,
    });
  } catch (error: any) {
    // Convert only timeout-owned aborts (or plain fetch aborts without a caller signal) to timeout errors.
    if (error?.name === 'AbortError' && (didTimeout || !sourceSignal?.aborted)) {
      throw new FetchTimeoutError(timeoutMs);
    }
    throw error;
  } finally {
    clearTimeout(timeoutId);
    sourceSignal?.removeEventListener?.('abort', abortFromSource);
  }
}
